import { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { getSitePickerActivationDelay, MouseLocation } from './sitePickerMenuAim';

const MOUSE_LOCATIONS_TRACKED = 3;

type Props<Site> = {
  activeSite: Site | null;
  activateSite: (site: Site) => void;
};

export const useSitePickerMenuAim = <Site>({ activeSite, activateSite }: Props<Site>) => {
  const menuRef = useRef<HTMLUListElement>(null);
  const mouseLocationsRef = useRef<MouseLocation[]>([]);
  const lastDelayLocationRef = useRef<MouseLocation | null>(null);
  const pendingSiteRef = useRef<Site | null>(null);
  const activationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearActivationTimeout = useCallback(() => {
    if (activationTimeoutRef.current) {
      clearTimeout(activationTimeoutRef.current);
      activationTimeoutRef.current = null;
    }
  }, []);

  const cancelPendingActivation = useCallback(() => {
    clearActivationTimeout();
    pendingSiteRef.current = null;
    lastDelayLocationRef.current = null;
  }, [clearActivationTimeout]);

  const resetCursorIntent = useCallback(() => {
    cancelPendingActivation();
    mouseLocationsRef.current = [];
  }, [cancelPendingActivation]);

  const recordMouseLocation = useCallback((event: MouseEvent<HTMLElement>) => {
    mouseLocationsRef.current.push({
      x: event.clientX,
      y: event.clientY,
    });

    if (mouseLocationsRef.current.length > MOUSE_LOCATIONS_TRACKED) {
      mouseLocationsRef.current.shift();
    }
  }, []);

  const requestActivation = useCallback(
    (site: Site) => {
      clearActivationTimeout();
      pendingSiteRef.current = site;

      const possiblyActivate = () => {
        if (pendingSiteRef.current !== site) {
          return;
        }

        const delay = getSitePickerActivationDelay({
          hasActiveSite: activeSite !== null,
          lastDelayLocation: lastDelayLocationRef.current,
          menuBounds: menuRef.current?.getBoundingClientRect() ?? null,
          mouseLocations: mouseLocationsRef.current,
        });

        if (delay) {
          lastDelayLocationRef.current = mouseLocationsRef.current[mouseLocationsRef.current.length - 1] ?? null;
          activationTimeoutRef.current = setTimeout(possiblyActivate, delay);
          return;
        }

        activateSite(site);
        pendingSiteRef.current = null;
        activationTimeoutRef.current = null;
        lastDelayLocationRef.current = null;
      };

      possiblyActivate();
    },
    [activeSite, activateSite, clearActivationTimeout],
  );

  const getCursorIntentState = useCallback(
    () => ({
      lastDelayLocation: lastDelayLocationRef.current,
      menuBounds: menuRef.current?.getBoundingClientRect() ?? null,
      mouseLocations: [...mouseLocationsRef.current],
    }),
    [],
  );

  useEffect(() => cancelPendingActivation, [cancelPendingActivation]);

  return {
    cancelPendingActivation,
    getCursorIntentState,
    menuRef,
    recordMouseLocation,
    requestActivation,
    resetCursorIntent,
  };
};
